"use client";

import { useState } from "react";

import {
  CalendarClock,
  Check,
  Handshake,
  Inbox,
  LifeBuoy,
  Linkedin,
  Mail,
  UserCheck,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_BASE_URL } from "@/config/api";
import { pushDataLayerEvent } from "@/lib/utils";

const CONTACT_CARDS = [
  {
    icon: LifeBuoy,
    title: "Support",
    description: "Existing user with a question?",
    link: { label: "support@folktale.io", href: "mailto:support@folktale.io" },
  },
  {
    icon: Mail,
    title: "General",
    description: "Anything else.",
    link: { label: "hello@folktale.io", href: "mailto:hello@folktale.io" },
  },
  {
    icon: Handshake,
    title: "Partnership",
    description:
      "Implementing partner, integration, or research collaboration.",
    link: {
      label: "partners@folktale.io",
      href: "mailto:partners@folktale.io",
    },
  },
  {
    icon: Linkedin,
    title: "LinkedIn",
    description: "Follow updates and product releases.",
    link: {
      label: "Visit our page",
      href: "https://www.linkedin.com/company/folktale",
      external: true,
    },
  },
];

type Status = "idle" | "loading" | "success" | "error";

type Submitted = {
  name: string;
  role: string;
  organisation: string;
  email: string;
};

export default function ContactContent() {
  const [orgSize, setOrgSize] = useState("");
  const [useCase, setUseCase] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [submitted, setSubmitted] = useState<Submitted | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");

    const data = new FormData(e.currentTarget);

    try {
      const res = await fetch(`${API_BASE_URL}/v2/contact-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          role: data.get("role"),
          organisation: data.get("organisation"),
          organisation_size: orgSize,
          country: data.get("country"),
          use_case: useCase,
          message: data.get("message"),
        }),
      });
      if (res.ok) {
        pushDataLayerEvent("contact_form_submit");
        setSubmitted({
          name: data.get("name") as string,
          role: data.get("role") as string,
          organisation: data.get("organisation") as string,
          email: data.get("email") as string,
        });
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <main className="flex-1">
      {/* Hero */}
      <section className="bg-[#f9f7f4] pt-12 pb-10 lg:pt-20 lg:pb-12">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#1a4d33]/15 bg-[#e8f5ee]/50 px-3 py-1 text-xs font-semibold tracking-wider text-[#1a4d33] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-[#c4623a]" />
              Contact
            </span>
            <h1 className="text-4xl leading-[1.05] font-medium tracking-tight text-balance md:text-5xl lg:text-6xl">
              Get in touch.
            </h1>
            <p className="text-muted-foreground mt-5 text-lg text-pretty md:text-xl">
              Enterprise, demo, support — pick the path that fits and we&apos;ll
              route it to the right team.
            </p>
          </div>
        </div>
      </section>

      {/* Form + aside */}
      <section className="bg-[#f9f7f4] pb-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:px-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border-border bg-card rounded-3xl border p-7 md:p-10">
              <h2 className="text-2xl font-medium">Enterprise enquiry</h2>
              <p className="text-muted-foreground mt-2 text-sm">
                Most enquiries get a reply within one business day.
              </p>

              {status === "success" && submitted ? (
                <div className="relative mt-8 overflow-hidden rounded-2xl border border-[#1a4d33]/30 bg-linear-to-br from-[#e8f5ee] via-[#f5f0e8] to-[rgba(196,98,58,0.15)] p-10 text-center">
                  {/* Decorative orbs */}
                  <div
                    className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl"
                    style={{ backgroundColor: "rgba(26,77,51,0.15)" }}
                  />
                  <div
                    className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full blur-3xl"
                    style={{ backgroundColor: "rgba(196,98,58,0.15)" }}
                  />

                  <div className="relative">
                    {/* Check icon */}
                    <div
                      className="animate-in zoom-in-50 mx-auto flex h-16 w-16 items-center justify-center rounded-full shadow-lg ring-8 ring-[rgba(26,77,51,0.10)] duration-500"
                      style={{ backgroundColor: "#1a4d33", color: "#f5f0e8" }}
                    >
                      <Check
                        className="h-8 w-8"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    </div>

                    <h3 className="mt-5 text-3xl font-medium tracking-tight">
                      Thanks, got it.
                    </h3>
                    <p className="text-foreground/75 mx-auto mt-3 max-w-md text-base">
                      We&apos;ve routed your enquiry to the right team. Expect a
                      reply within one business day.
                    </p>

                    {/* Pulsing badge */}
                    <div
                      className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium backdrop-blur-sm"
                      style={{
                        borderColor: "rgba(26,77,51,0.20)",
                        backgroundColor: "rgba(245,240,232,0.60)",
                        color: "#1a4d33",
                      }}
                    >
                      <span className="relative flex h-2 w-2">
                        <span
                          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                          style={{ backgroundColor: "#1a4d33" }}
                        />
                        <span
                          className="relative inline-flex h-2 w-2 rounded-full"
                          style={{ backgroundColor: "#1a4d33" }}
                        />
                      </span>
                      Reply within 1 business day
                    </div>

                    {/* Submitted details */}
                    <div
                      className="border-border/60 mx-auto mt-7 max-w-sm rounded-xl border p-4 text-left backdrop-blur-sm"
                      style={{ backgroundColor: "rgba(245,240,232,0.70)" }}
                    >
                      <p className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                        Submitted details
                      </p>
                      <dl className="mt-2 space-y-1.5 text-sm">
                        {(
                          [
                            { label: "Name", value: submitted.name },
                            { label: "Role", value: submitted.role },
                            {
                              label: "Organisation",
                              value: submitted.organisation,
                            },
                            { label: "Email", value: submitted.email },
                          ] as const
                        )
                          .filter((r) => r.value)
                          .map(({ label, value }) => (
                            <div
                              key={label}
                              className="flex items-baseline justify-between gap-3"
                            >
                              <dt className="text-muted-foreground">{label}</dt>
                              <dd className="text-foreground truncate font-medium">
                                {value}
                              </dd>
                            </div>
                          ))}
                      </dl>
                    </div>

                    {/* What happens next */}
                    <div
                      className="border-border/60 mx-auto mt-7 max-w-md rounded-xl border p-5 text-left backdrop-blur-sm"
                      style={{ backgroundColor: "rgba(245,240,232,0.70)" }}
                    >
                      <p className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                        What happens next
                      </p>
                      <ol className="mt-3 space-y-3">
                        {[
                          {
                            icon: Inbox,
                            title: "Confirmation email",
                            desc: "A receipt is on its way to your inbox right now.",
                          },
                          {
                            icon: UserCheck,
                            title: "Routed to a specialist",
                            desc: "A solutions lead matched to your use case will pick this up.",
                          },
                          {
                            icon: CalendarClock,
                            title: "Reply within 1 business day",
                            desc: "They'll be in touch to schedule a 30-minute intro call.",
                          },
                        ].map(({ icon: Icon, title, desc }) => (
                          <li key={title} className="flex items-start gap-3">
                            <div
                              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                              style={{
                                backgroundColor: "rgba(26,77,51,0.10)",
                                color: "#1a4d33",
                              }}
                            >
                              <Icon className="h-4 w-4" aria-hidden />
                            </div>
                            <div>
                              <p className="text-foreground text-sm font-medium">
                                {title}
                              </p>
                              <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                                {desc}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Name">
                      <input
                        name="name"
                        required
                        className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                      />
                    </Field>
                    <Field label="Role">
                      <input
                        name="role"
                        className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                      />
                    </Field>
                  </div>

                  <Field label="Work email">
                    <input
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@organisation.org"
                      className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                    />
                  </Field>

                  <Field label="Organisation">
                    <input
                      name="organisation"
                      className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                    />
                  </Field>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Organisation size">
                      <Select value={orgSize} onValueChange={setOrgSize}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-50">1–50</SelectItem>
                          <SelectItem value="51-200">51–200</SelectItem>
                          <SelectItem value="201-1000">201–1000</SelectItem>
                          <SelectItem value="1000+">1000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Country">
                      <input
                        name="country"
                        className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:ring-1 focus-visible:outline-none"
                      />
                    </Field>
                  </div>

                  <Field label="Primary use case">
                    <Select value={useCase} onValueChange={setUseCase}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select use case" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERL & Evaluation">
                          MERL & Evaluation
                        </SelectItem>
                        <SelectItem value="CSR & ESG">CSR & ESG</SelectItem>
                        <SelectItem value="Government Program">
                          Government Program
                        </SelectItem>
                        <SelectItem value="Communications & Impact">
                          Communications & Impact
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Message (optional)">
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="Tell us how we can help you today."
                      className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
                    />
                  </Field>

                  {status === "error" && (
                    <p className="text-destructive text-sm">
                      Something went wrong. Please try again or email us
                      directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex h-10 w-full items-center justify-center rounded-md px-8 text-sm font-medium shadow transition-colors disabled:opacity-50 sm:w-auto"
                    style={{ backgroundColor: "#1a4d33", color: "#f5f0e8" }}
                  >
                    {status === "loading" ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Aside */}
          <aside className="lg:col-span-5">
            <div className="space-y-3">
              {CONTACT_CARDS.map(({ icon: Icon, title, description, link }) => (
                <div
                  key={title}
                  className="border-border bg-card rounded-2xl border p-5"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg"
                      style={{
                        backgroundColor: "rgba(196,98,58,0.10)",
                        color: "#c4623a",
                      }}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </div>
                    <h3 className="text-lg font-medium">{title}</h3>
                  </div>
                  <p className="text-muted-foreground mt-2 text-sm">
                    {description}
                  </p>
                  <a
                    href={link.href}
                    {...("external" in link && link.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="mt-3 inline-block text-sm font-medium hover:underline"
                    style={{ color: "#1a4d33" }}
                  >
                    {link.label}
                  </a>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
